import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { prepareRequest } from '@atlas/core';
import { Form } from '@atlas/react-forms';
import { Button } from '@atlas/ui';

import ErrorFields from 'common/Form/ErrorFields';
import { fieldComponents } from 'common/Form/formConfig';
import CaptchaInput from 'common/Form/FormWithCaptcha/CaptchaInput';
import useFormCaptcha from 'common/Form/FormWithCaptcha/useFormCaptcha';
import GlobalErrors from 'common/Form/GlobalErrors';

const templatesWithErrorFields = {
  BY_EMAIL: true,
  BY_PHONE: true,
  MULTI_STEP: true,
};

const ContainerComponent = ({
  autoComplete = false,
  buttonId,
  buttonText = '',
  centerContent = null,
  children,
  className = '',
  disableButton,
  disablePristine = false,
  fieldErrors,
  fields = [],
  footerContent = null,
  formNumber,
  formType,
  globalErrors = [],
  isLoading = false,
  isSubmitButtonAlwaysEnabled,
  leftSideSubmitContent = null,
  loadingDeferFields,
  loadingFields,
  // hasActiveFields = false,
  onSetGlobalErrors = () => {},
  onSubmit,
  onTrackedFieldChanges,
  touchedFields,
  trackedFields,
  trailingCenterContent = null,
  validatedDeferFields,
  values,
}) => {
  const captchaRef = useRef();
  const {
    input: captchaInput,
    isRequired: isCaptchaRequired,
    key: captchaKey,
    setInput: onCaptchaChange,
  } = useFormCaptcha();

  const [errorFields, setErrorFields] = useState([]);
  const [showErrorFields, setShowErrorFields] = useState(false);

  const isFormInvalid =
    (isCaptchaRequired && !captchaInput) ||
    isLoading ||
    globalErrors.length ||
    Object.values(loadingFields).some(isLoading => isLoading) ||
    Object.values(fieldErrors).some(errorList => errorList.length !== 0) ||
    (disablePristine && Object.values(touchedFields).every(isTouched => !isTouched)) ||
    disableButton;

  const isDisabled = !isSubmitButtonAlwaysEnabled && isFormInvalid;

  const handleErrorFields = useCallback(() => {
    setShowErrorFields(true);
    setErrorFields(
      fields.reduce((fieldsToReturn, field) => {
        const currentFieldErrors = fieldErrors[field.name];

        if (!currentFieldErrors || currentFieldErrors.length === 0) {
          return fieldsToReturn;
        }

        return [...fieldsToReturn, field.label];
      }, []),
    );
  }, [fields, fieldErrors]);

  const handleCaptchaChange = useCallback(
    value => {
      onSetGlobalErrors([]);
      onCaptchaChange(value);
    },
    [onCaptchaChange, onSetGlobalErrors],
  );

  const handleSubmitWithCaptcha = useCallback(
    event => {
      if (isCaptchaRequired && !captchaInput) {
        return;
      }

      onSubmit(event);

      if (captchaRef && captchaRef.current && captchaRef.current.reset) {
        captchaRef.current.reset();
      }
    },
    [isCaptchaRequired, captchaInput, onSubmit],
  );

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Enter' && event.shiftKey === false) {
        event.preventDefault();
        handleSubmitWithCaptcha(event);
      }
    },
    [handleSubmitWithCaptcha],
  );
  const formClassName = cx('form', className);

  useEffect(() => {
    setShowErrorFields(false);
  }, [formType?.id, formNumber]);

  useEffect(() => {
    if (isFormInvalid && validatedDeferFields && !loadingDeferFields) {
      handleErrorFields();
    }
  }, [handleErrorFields, isFormInvalid, loadingDeferFields, validatedDeferFields]);

  const trackValues = useRef({});
  useEffect(() => {
    if (!trackedFields || !onTrackedFieldChanges) {
      return;
    }

    trackedFields.forEach(fieldName => {
      const touched = touchedFields[fieldName];
      const errors = fieldErrors[fieldName];
      const checked = trackValues.current[fieldName];
      const value = values[fieldName];
      const valid = !errors || !errors.length;

      if (!checked && touched && valid && value) {
        // Track the completion of the valid value input
        // Broadcast changes only once
        onTrackedFieldChanges(fieldName);
        trackValues.current[fieldName] = true;
      }
    });
  }, [values, trackedFields, touchedFields, onTrackedFieldChanges, fieldErrors]);

  return (
    <form
      autoComplete={autoComplete ? 'on' : 'off'}
      className={formClassName}
      onKeyDown={handleKeyDown}
    >
      {showErrorFields &&
        errorFields.length > 0 &&
        templatesWithErrorFields[formType?.templateName] && (
          <ErrorFields errorFields={errorFields} />
        )}
      {children}
      {isCaptchaRequired ? (
        <div className="captcha-container">
          <CaptchaInput captchaKey={captchaKey} onChange={handleCaptchaChange} ref={captchaRef} />
        </div>
      ) : null}
      {trailingCenterContent != null ? (
        <div className="form-amount-center-stack">
          {centerContent}
          <GlobalErrors globalErrors={globalErrors} />
          {trailingCenterContent}
        </div>
      ) : (
        <>
          <GlobalErrors globalErrors={globalErrors} />
          {centerContent}
        </>
      )}
      <div className="sticky-bottom-buttons-container">
        {leftSideSubmitContent}
        <Button
          className="submit-button"
          disabled={isDisabled || loadingDeferFields}
          id={buttonId}
          label={buttonText}
          loading={isLoading || loadingDeferFields}
          onClick={handleSubmitWithCaptcha}
          size="big"
          type="primary"
        />
      </div>
      {footerContent}
    </form>
  );
};

ContainerComponent.propTypes = {
  buttonId: PropTypes.string,
  buttonText: PropTypes.string,
  centerContent: PropTypes.node,
  className: PropTypes.string,
  disableButton: PropTypes.bool,
  // hasActiveFields: PropTypes.bool,
  disablePristine: PropTypes.bool,
  fields: PropTypes.array,
  footerContent: PropTypes.node,
  formNumber: PropTypes.number,
  formType: PropTypes.shape({
    id: PropTypes.string,
  }),
  globalErrors: PropTypes.array,
  isLoading: PropTypes.bool,
  leftSideSubmitContent: PropTypes.node,
  onSetGlobalErrors: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  trailingCenterContent: PropTypes.node,
};

const FormMobile = forwardRef(
  (
    {
      action,
      additionalValues = null,
      autoComplete = false,
      buttonId,
      buttonText = '',
      centerContent = null,
      className = '',
      disableButton = false,
      disablePristine = false,
      footerContent = null,
      form,
      formType,
      id,
      initialValues = {},
      isSubmitButtonAlwaysEnabled,
      leftSideSubmitContent = null,
      onError = () => {},
      onSubmit,
      onTrackedFieldChanges,
      trackedFields,
      trailingCenterContent = null,
      validateRequestHeaders,
    },
    ref,
  ) => {
    const validateFieldAsyncHandler = useCallback(
      async field => {
        const { disabled, name: fieldName, value } = field;

        if (disabled || value === null || value === undefined || value.length === 0) {
          return [];
        }

        const phoneValue = value.length < 3 && value[0] === '+' ? '' : value;

        const request = prepareRequest({
          ...(validateRequestHeaders && { headers: validateRequestHeaders }),
          method: 'POST',
          url: 'client/form:validate-field',
        });

        let errors = [];

        try {
          await request({
            fieldName,
            formId: id,
            value: fieldName === 'PHONE' ? phoneValue : value,
          });
        } catch (error) {
          const newErrors = error?.errors?.[0]?.violations;

          if (newErrors && newErrors.length) {
            errors = newErrors;
          }
        }

        return errors;
      },
      [id, validateRequestHeaders],
    );

    return (
      <Form
        ContainerComponent={ContainerComponent}
        ContainerComponentProps={{
          buttonId,
          buttonText,
          centerContent,
          className,
          disableButton,
          footerContent,
          formType,
          isSubmitButtonAlwaysEnabled,
          leftSideSubmitContent,
          onTrackedFieldChanges,
          trackedFields,
          trailingCenterContent,
        }}
        action={action}
        additionalValues={additionalValues}
        autoComplete={autoComplete}
        disableButton={disableButton}
        disablePristine={disablePristine}
        enableButton={isSubmitButtonAlwaysEnabled}
        fieldComponents={fieldComponents}
        form={form}
        initialValues={initialValues}
        onError={onError}
        onSubmit={onSubmit}
        onValidateFieldAsync={validateFieldAsyncHandler}
        ref={ref}
        steps={formType?.steps}
      />
    );
  },
);

FormMobile.propTypes = {
  action: PropTypes.func.isRequired,
  additionalValues: PropTypes.object,
  autoComplete: PropTypes.bool,
  buttonId: PropTypes.string,
  buttonText: PropTypes.string,
  centerContent: PropTypes.node,
  className: PropTypes.string,
  disableButton: PropTypes.bool,
  disablePristine: PropTypes.bool,
  footerContent: PropTypes.node,
  form: PropTypes.object.isRequired,
  formType: PropTypes.object,
  id: PropTypes.string.isRequired,
  initialValues: PropTypes.object,
  leftSideSubmitContent: PropTypes.node,
  onError: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  trailingCenterContent: PropTypes.node,
};

export default FormMobile;
